import { Table, Text } from '@mantine/core';

import {
	SCUD_AUDIT_ACTIONS,
	SCUD_AUDIT_ALEMBIC_AUTHOR,
	SCUD_AUDIT_ALEMBIC_PREFIX,
	SCUD_AUDIT_AUTHORS,
	SCUD_AUDIT_FIELDS,
	SCUD_EMPTY,
	SCUD_MATCHED_BY,
	SCUD_STATUS,
} from '@/shared/constant/scudMapping';
import {
	IScudAuditEntry,
	IScudMappingReason,
	TScudMatchStatus,
} from '@/shared/types/scudMapping.types';

import { formatScudDateTime, reasonLabelById } from './utils';

/** Поля, которые при создании записи загрузкой показываем; остальное — служебное */
const CREATE_FIELDS = ['match_status', 'full_name_federation', 'matched_by'];

type HistoryProps = {
	history: IScudAuditEntry[];
	reasons: IScudMappingReason[];
};

const toText = (value: unknown) => {
	if (typeof value === 'string') {
		return value;
	}

	if (typeof value === 'number' || typeof value === 'boolean') {
		return value.toString();
	}

	return JSON.stringify(value);
};

const formatValue = (key: string, value: unknown, reasons: IScudMappingReason[]): string => {
	if (value === null || value === undefined || value === '') {
		return SCUD_EMPTY;
	}

	const text = toText(value);

	switch (key) {
		case 'match_status':
			return SCUD_STATUS[value as TScudMatchStatus]?.label ?? text;
		case 'reason_id':
			return reasonLabelById(reasons, text) ?? text;
		case 'guard_confirmed':
			return value ? 'да' : 'нет';
		case 'matched_by':
			return SCUD_MATCHED_BY[text] ?? text;
		case 'confirmed_at':
			return formatScudDateTime(text);
		default:
			return text;
	}
};

const authorText = (changedBy: string | null | undefined) => {
	if (!changedBy) {
		return SCUD_EMPTY;
	}

	if (changedBy.startsWith(SCUD_AUDIT_ALEMBIC_PREFIX)) {
		return SCUD_AUDIT_ALEMBIC_AUTHOR;
	}

	return SCUD_AUDIT_AUTHORS[changedBy] ?? changedBy;
};

/** Одна строка на запись журнала: служебные пометки, затем изменённые поля через «;» */
const entryText = (entry: IScudAuditEntry, reasons: IScudMappingReason[]) => {
	const oldValues = entry.old_values ?? {};
	const newValues = entry.new_values ?? {};
	const keys = Array.from(new Set([...Object.keys(oldValues), ...Object.keys(newValues)]));
	const notes = keys
		.filter((key) => key.startsWith('_'))
		.map((key) => newValues[key] ?? oldValues[key])
		.filter((value) => value !== null && value !== undefined && value !== '')
		.map(toText);
	const fields = keys
		.filter((key) => !key.startsWith('_'))
		.filter(
			(key) =>
				entry.action !== 'CREATE' ||
				(CREATE_FIELDS.includes(key) && newValues[key] !== null && newValues[key] !== ''),
		)
		// Рядом с ФИО id сотрудника ничего не добавляет
		.filter((key) => key !== 'person_id_federation' || !keys.includes('full_name_federation'));

	const changes = fields.map((key) => {
		const label = SCUD_AUDIT_FIELDS[key] ?? key;
		const next = formatValue(key, newValues[key], reasons);

		if (entry.action === 'CREATE') {
			return `${label}: ${next}`;
		}

		return `${label}: ${formatValue(key, oldValues[key], reasons)} → ${next}`;
	});

	const prefix =
		entry.action === 'UPDATE' ? [] : [SCUD_AUDIT_ACTIONS[entry.action] ?? entry.action];

	return [...prefix, ...notes, ...changes].join('; ') || SCUD_EMPTY;
};

export const ScudMappingHistory = ({ history, reasons }: HistoryProps) => {
	if (!history.length) {
		return (
			<Text size="sm" c="dimmed">
				Изменений нет
			</Text>
		);
	}

	return (
		<Table withTableBorder verticalSpacing="xs">
			<Table.Tbody>
				{history.map((entry) => {
					const author = authorText(entry.changed_by);

					return (
						<Table.Tr key={entry.id}>
							<Table.Td w={220} style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
								<Text size="sm" c="dimmed">
									{formatScudDateTime(entry.changed_at)} · {author}
								</Text>
							</Table.Td>
							<Table.Td>
								<Text size="sm">{entryText(entry, reasons)}</Text>
							</Table.Td>
						</Table.Tr>
					);
				})}
			</Table.Tbody>
		</Table>
	);
};
